import React from 'react'
import styled from 'styled-components'

const TooltipContainer = styled.div`
  min-width: 200px;
  max-width: 300px;

  @media (max-width: 768px) {
    min-width: 180px;
    max-width: 250px;
  }

  @media (max-width: 480px) {
    min-width: 160px;
    max-width: 200px;
  }
`

const PermitTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #1a1a1a;
  line-height: 1.3;
`

const PermitNumber = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #00ade6;
  margin-bottom: 8px;
`

const PermitDetails = styled.div`
  font-size: 12px;
  color: #6b6b6b;
  line-height: 1.4;

  div {
    margin-bottom: 4px;
  }
`

const Badge = styled.span<{ $statusColor?: string }>`
  display: inline-block;
  padding: 2px 6px;
  background: ${({ $statusColor }) =>
    $statusColor ? `${$statusColor}20` : 'rgba(0, 173, 230, 0.1)'};
  color: ${({ $statusColor }) => $statusColor || '#00ade6'};
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  margin: 2px;
`

const BadgeContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 4px;
  margin: -2px;
`

interface BuildingPermitTooltipProps {
  properties: any
}

const getStatusColor = (status: string): string => {
  const statusLower = status?.toLowerCase() || ''
  if (
    statusLower.includes('accepté') ||
    statusLower.includes('accepted') ||
    statusLower.includes('approved')
  )
    return '#10b981'
  if (
    statusLower.includes('refusé') ||
    statusLower.includes('rejected') ||
    statusLower.includes('refused')
  )
    return '#ef4444'
  if (
    statusLower.includes('instruction') ||
    statusLower.includes('pending') ||
    statusLower.includes('attente')
  )
    return '#f59e0b'
  if (statusLower.includes('terminé') || statusLower.includes('completed'))
    return '#6b7280'
  return '#00ade6'
}

const getCategoryLabel = (category: string): string => {
  const categoryMap: Record<string, string> = {
    'construction nouvelle': 'New Construction',
    development: 'Development',
    renovation: 'Renovation',
    rénovation: 'Renovation',
    transformation: 'Transformation',
    surélévation: 'Elevation',
    elevation: 'Elevation',
    démolition: 'Demolition',
    demolition: 'Demolition',
    régularisation: 'Regularisation',
    regularisation: 'Regularisation',
  }
  return categoryMap[category?.toLowerCase()] || category
}

const getStatusLabel = (status: string): string => {
  const statusMap: Record<string, string> = {
    'en instruction': 'Under Review',
    instruction: 'Under Review',
    enregistrement: 'Registered',
    accepté: 'Approved',
    accepted: 'Approved',
    approved: 'Approved',
    'en attente': 'Pending',
    pending: 'Pending',
    refusé: 'Rejected',
    refused: 'Rejected',
    rejected: 'Rejected',
    terminé: 'Completed',
    completed: 'Completed',
  }
  return statusMap[status?.toLowerCase()] || status
}

export const BuildingPermitTooltip: React.FC<BuildingPermitTooltipProps> = ({
  properties,
}) => {
  const {
    id,
    primary_official_id,
    fao_id,
    description,
    city_name,
    postal_codes_names,
    regbl_classification_general,
    latest_status_db,
    inquiry_start_date,
  } = properties

  const permitNumber = primary_official_id || fao_id || `ID-${id}`
  const category = regbl_classification_general
  const status = latest_status_db
  const postalCode = postal_codes_names?.[0]?.split(' ')?.[0]
  const location =
    postalCode && city_name ? `${postalCode} ${city_name}` : city_name

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return null
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    } catch {
      return null
    }
  }

  return (
    <TooltipContainer>
      <PermitNumber>Permit #{permitNumber}</PermitNumber>

      {description && (
        <PermitTitle>
          {description.length > 60
            ? `${description.substring(0, 60)}...`
            : description}
        </PermitTitle>
      )}

      <PermitDetails>
        {location && <div>{location}</div>}
        {inquiry_start_date && (
          <div>Filed: {formatDate(inquiry_start_date)}</div>
        )}
      </PermitDetails>

      <BadgeContainer>
        {status && (
          <Badge $statusColor={getStatusColor(status)}>
            {getStatusLabel(status)}
          </Badge>
        )}
        {category && <Badge>{getCategoryLabel(category)}</Badge>}
      </BadgeContainer>
    </TooltipContainer>
  )
}
